/**
 * Process-wide room session: at most one HostRoom or ClientRoom at a time.
 * The hook (`useRoom`) and the join route talk to this module.
 */
import type { GameConfig, GameState } from '@/engine/types';
import { useSettings } from '@/store/settings';
import { useGame } from '@/store/game';
import { createRelayTransport, relayUrl } from './relayTransport';
import {
  ClientRoom,
  HostRoom,
  readGuestMemo,
  readHostMemo,
  seatsFromGame,
  wireDiag,
  writeGuestMemo,
  writeHostMemo,
  type GuestProfile,
} from './room';
import { isValidCode } from './protocol';
import { useRoomStore } from './roomStore';
import { TransportError } from './transport';
import type { PlayerColorId } from '@/lib/options';

let host: HostRoom | null = null;
let client: ClientRoom | null = null;
let variantProvider: () => string | null = () => null;

/** The lobby can register how to read its selected variant for guests' lobby view. */
export function setVariantProvider(fn: () => string | null): void {
  variantProvider = fn;
  // Guests learn about the variant on the next lobby broadcast.
}

export function currentHost(): HostRoom | null {
  return host;
}

function failRoom(
  err: unknown,
  fallback: string,
  role: 'none' | 'client',
  code?: string
) {
  const message = err instanceof TransportError ? err.message : fallback;
  useRoomStore.getState().patch({
    status: 'error',
    role,
    ...(code ? { code } : {}),
    error: message,
    connection: 'lost',
  });
}

/** Close whatever is active without forgetting persisted memos. */
function teardown(): void {
  host?.close(false);
  host = null;
  client?.leave(false);
  client = null;
  const store = useGame.getState();
  if (store.role !== 'local') {
    store.setInterceptor(null);
    store.setRole('local');
  }
  useRoomStore.getState().reset();
}

export async function hostRoom(): Promise<string> {
  teardown();
  const name = useSettings.getState().playerName.trim() || 'Host';
  const transport = createRelayTransport();
  wireDiag(transport, relayUrl());
  const room = new HostRoom(transport, name, () => variantProvider());
  host = room;
  try {
    await room.open();
  } catch (err) {
    host = null;
    transport.destroy();
    failRoom(err, 'Could not open a room.', 'none');
    throw err;
  }
  return room.code;
}

export async function joinRoom(
  code: string,
  profile: GuestProfile
): Promise<void> {
  // React may invoke a join effect twice: reuse the connect in flight.
  if (client && client.code === code && client.pending) return client.pending;
  const remembered = readGuestMemo();
  const playerId =
    remembered && remembered.code === code ? remembered.playerId : null;
  teardown();
  if (!isValidCode(code)) {
    useRoomStore.getState().patch({
      status: 'error',
      role: 'client',
      code,
      error: 'Enter the 5-character room code.',
      connection: 'lost',
    });
    throw new Error('invalid code');
  }
  // A local game must not shadow the host's state.
  useGame.getState().quit();
  const transport = createRelayTransport();
  wireDiag(transport, relayUrl());
  const room = new ClientRoom(transport, code, profile, { playerId });
  room.probe = () => probeRoom(code);
  client = room;
  const attempt = (async () => {
    try {
      await room.connect();
    } catch (err) {
      if (client === room) client = null;
      transport.destroy();
      failRoom(err, 'Could not connect to that room.', 'client', code);
      // Forget the seat only when the relay confirms the room is gone.
      if (playerId) {
        const kind = err instanceof TransportError ? err.kind : 'unknown';
        if (kind === 'ended') writeGuestMemo(null);
        else if (kind === 'peer-unavailable') {
          const p = await probeRoom(code);
          if (!p || !p.exists || p.ended) writeGuestMemo(null);
        }
      }
      throw err;
    } finally {
      room.pending = null;
    }
  })();
  room.pending = attempt;
  return attempt;
}

export function startHostedGame(config: GameConfig): GameState {
  if (!host) throw new Error('Not hosting a room');
  return host.startGame(config);
}

/** Host only: the AI plays the current turn for a remote seat, once. */
export function playTurnForSeat(playerId: string): void {
  host?.playTurnForSeat(playerId);
}

/** Host only: permanently hand a remote seat to an AI (until they return). */
export function handSeatToAi(playerId: string): void {
  host?.handSeatToAi(playerId);
}

/** Client: retry right away, or rejoin after the connection was marked lost. */
export function retryConnection(): void {
  if (client) {
    client.retryNow();
    return;
  }
  const memo = readGuestMemo();
  if (memo) {
    void joinRoom(memo.code, { name: memo.name, color: memo.color }).catch(
      () => undefined
    );
  }
}

/**
 * A hosted game persisted on this device that can be reopened: the room
 * code plus the game, or null. Read by the lobby's resume card.
 */
export function recoverableRoom(): { code: string; game: GameState } | null {
  if (host) return null;
  const memo = readHostMemo();
  const game = useGame.getState().game;
  if (!memo || !game || game.phase !== 'playing') return null;
  const ids = new Set(memo.remoteIds);
  if (!game.players.some(p => ids.has(p.id))) return null;
  return { code: memo.code, game };
}

/** Host: reopen a persisted room with the same code and game. */
export async function reopenRoom(): Promise<void> {
  const memo = readHostMemo();
  const game = useGame.getState().game;
  if (!memo || !game) throw new Error('Nothing to reopen');
  teardown();
  const transport = createRelayTransport();
  wireDiag(transport, relayUrl());
  const room = new HostRoom(
    transport,
    memo.hostName,
    () => variantProvider(),
    memo.code,
    { token: memo.token, seats: seatsFromGame(game, memo.remoteIds) }
  );
  host = room;
  try {
    await room.open();
  } catch (err) {
    host = null;
    transport.destroy();
    failRoom(err, 'Could not reopen the room.', 'none');
    const kind = err instanceof TransportError ? err.kind : 'unknown';
    if (kind === 'ended' || kind === 'peer-unavailable') writeHostMemo(null);
    throw err;
  }
  room.resumeGame(game);
}

/**
 * A guest seat persisted on this device that can be rejoined (memo exists
 * and is under 24 h old), or null.
 */
export function recoverableJoin(): {
  code: string;
  name: string;
  color: PlayerColorId;
} | null {
  if (client) return null;
  const memo = readGuestMemo();
  if (!memo) return null;
  return { code: memo.code, name: memo.name, color: memo.color };
}

export function leaveRoom(): void {
  host?.close();
  host = null;
  client?.leave();
  client = null;
  writeHostMemo(null);
  writeGuestMemo(null);
  const store = useGame.getState();
  if (store.role !== 'local') {
    store.setInterceptor(null);
    store.setRole('local');
  }
  useRoomStore.getState().reset();
}

/** Clear an error without tearing anything else down. */
export function clearRoomError(): void {
  const s = useRoomStore.getState();
  if (s.status === 'error' || s.status === 'ended') s.reset();
}

/** What the relay knows about a room right now (HTTP probe, no socket). */
export interface RoomProbe {
  exists: boolean;
  ended: boolean;
  hostPresent: boolean;
  guestCount: number;
}

/**
 * Ask the relay about a room without attaching (HTTP). Resolves null when
 * the relay cannot be reached or the code is malformed.
 */
export async function probeRoom(code: string): Promise<RoomProbe | null> {
  const c = code.toUpperCase();
  if (!isValidCode(c)) return null;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 6000);
    const res = await fetch(`${relayUrl()}/room/${c}/status`, {
      signal: ctrl.signal,
      cache: 'no-store',
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<RoomProbe>;
    return {
      exists: !!body.exists,
      ended: !!body.ended,
      hostPresent: !!body.hostPresent,
      guestCount: Number(body.guestCount ?? 0),
    };
  } catch {
    return null;
  }
}
